<script setup lang="ts">
// The pixel designer for profile pictures and space symbols: a 7×7 grid (the generated
// marks' grid), the server's palette, mirror painting, and live previews at real sizes.
import { computed, ref } from 'vue'
import { describeError } from '../api/http'
import { fitsPalette, formatPixels, fromIdenticon, INK, isBlank, paint, parsePixels, SIDE, type PixelIcon } from '../lib/pixels'
import { useSession } from '../stores/session'
import { conformIcon } from '../lib/color'
import Icon from './Icon.vue'
import Modal from './Modal.vue'
import PixelArt from './PixelArt.vue'

const props = defineProps<{
  title: string
  /** The current design, if any. */
  initial?: string | null
  /** Id whose generated mark "Reset" restores (in `seedColor`, else the first palette colour). */
  seedId: string
  seedColor?: string
  /** Saves the design (text), or null to remove it. */
  action: (pixels: string | null) => Promise<unknown>
}>()
const emit = defineEmits<{ close: [] }>()
const session = useSession()

// Monochrome first (white on dark, black on light), then the server's colours.
const palette = computed(() => [INK, ...session.palette])
const color = ref<string | null>(INK)
const mirror = ref(true)
const seed = () => fromIdenticon(props.seedId, props.seedColor ?? INK)
const existing = parsePixels(props.initial)
const icon = ref<PixelIcon>(existing ? conformIcon(existing, palette.value) : seed())
const busy = ref(false)
const error = ref<string | null>(null)

const offPalette = computed(() => !fitsPalette(icon.value, palette.value))

// Painting: press and drag across cells (pointer events, so touch works too).
const grid = ref<HTMLElement | null>(null)
let painting = false
let last = -1
function cellAt(e: PointerEvent): [number, number] | null {
  const r = grid.value!.getBoundingClientRect()
  const x = Math.floor(((e.clientX - r.left) / r.width) * SIDE)
  const y = Math.floor(((e.clientY - r.top) / r.height) * SIDE)
  return x >= 0 && x < SIDE && y >= 0 && y < SIDE ? [x, y] : null
}
function paintAt(e: PointerEvent): void {
  const at = cellAt(e)
  if (!at || at[1] * SIDE + at[0] === last) return
  last = at[1] * SIDE + at[0]
  icon.value = paint(icon.value, at[0], at[1], color.value, mirror.value)
}
function down(e: PointerEvent): void {
  painting = true
  last = -1
  grid.value?.setPointerCapture(e.pointerId)
  paintAt(e)
}
function move(e: PointerEvent): void {
  if (painting) paintAt(e)
}
function up(): void {
  painting = false
}

function clear(): void {
  icon.value = { colors: [palette.value[0]!], cells: new Array<number>(SIDE * SIDE).fill(0) }
}
function reset(): void {
  icon.value = seed()
}

async function run(value: string | null): Promise<void> {
  busy.value = true
  error.value = null
  try {
    await props.action(value)
    emit('close')
  } catch (e) {
    error.value = describeError(e)
  } finally {
    busy.value = false
  }
}
const save = () => run(isBlank(icon.value) ? null : formatPixels(icon.value))
</script>

<template>
  <Modal :title="title" @close="emit('close')">
    <div class="space-y-4" data-testid="pixel-editor">
      <div class="flex items-start gap-5 max-sm:flex-col">
        <!-- Canvas -->
        <div
          ref="grid"
          class="grid aspect-square w-60 shrink-0 cursor-crosshair touch-none grid-cols-7 gap-px border border-border-strong bg-border select-none"
          data-testid="pixel-grid"
          @pointerdown.prevent="down"
          @pointermove="move"
          @pointerup="up"
          @pointercancel="up"
        >
          <div
            v-for="(c, i) in icon.cells"
            :key="i"
            :style="{ background: !c ? 'var(--color-bg-primary)' : icon.colors[c - 1] === INK ? 'var(--color-text-primary)' : icon.colors[c - 1] }"
            :data-testid="`pixel-${i % SIDE}-${Math.floor(i / SIDE)}`"
            :data-color="c ? icon.colors[c - 1] : ''"
          />
        </div>

        <!-- Previews at real sizes -->
        <div class="space-y-3">
          <div class="label mb-1">Preview</div>
          <div class="flex items-end gap-2">
            <div
              v-for="px in [48, 36, 24, 16]"
              :key="px"
              class="border border-border bg-bg-primary"
              :style="{ width: `${px}px`, height: `${px}px` }"
            >
              <PixelArt :icon="icon" />
            </div>
          </div>
          <label class="flex cursor-pointer items-center gap-2 text-sm">
            <input v-model="mirror" type="checkbox" data-testid="pixel-mirror" />
            Mirror
          </label>
          <div class="flex flex-wrap gap-2">
            <button type="button" class="btn-ghost px-2" data-testid="pixel-clear" @click="clear">Clear</button>
            <button type="button" class="btn-ghost px-2" data-testid="pixel-reset" @click="reset">Reset</button>
          </div>
        </div>
      </div>

      <!-- Palette (set by the server's admins) -->
      <div>
        <div class="label">Colour</div>
        <div class="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Colour">
          <button
            v-for="c in palette"
            :key="c"
            type="button"
            role="radio"
            class="h-7 w-7 border"
            :class="color === c ? 'border-ink outline outline-1 outline-offset-1 outline-ink' : 'border-border'"
            :style="{ background: c === INK ? 'linear-gradient(135deg, #fff 50%, #000 50%)' : c }"
            :aria-checked="color === c"
            :aria-label="c === INK ? 'Monochrome (white on dark, black on light)' : c"
            :title="c === INK ? 'Monochrome: white on dark, black on light' : c"
            :data-testid="`pixel-color-${c}`"
            @click="color = c"
          />
          <button
            type="button"
            role="radio"
            class="flex h-7 w-7 items-center justify-center border text-text-muted"
            :class="color === null ? 'border-ink text-text-primary outline outline-1 outline-offset-1 outline-ink' : 'border-border'"
            :aria-checked="color === null"
            aria-label="Eraser"
            title="Eraser"
            data-testid="pixel-eraser"
            @click="color = null"
          >
            <Icon name="eraser" />
          </button>
        </div>
      </div>

      <p v-if="offPalette" class="text-sm text-signal" data-testid="pixel-off-palette">
        Some colours aren't in this server's palette any more. Repaint them to save.
      </p>
      <p v-if="error" class="text-sm text-error">{{ error }}</p>
      <div class="flex flex-wrap justify-end gap-2">
        <button v-if="initial" type="button" class="btn-ghost mr-auto" :disabled="busy" data-testid="pixel-remove" @click="run(null)">
          Remove design
        </button>
        <button type="button" class="btn-ghost" @click="emit('close')">Cancel</button>
        <button class="btn-primary" :disabled="busy || offPalette" data-testid="pixel-save" @click="save">Save</button>
      </div>
    </div>
  </Modal>
</template>
